/**
 * OverviewTab — Identity + engine + delivery + terminology (editable).
 *
 * Edits here flow to the active profile row; they do NOT auto-deploy —
 * they only update the in-place `profile_json`. A new version bump is
 * handled by the backend semver policy (ADR-102) when the admin clicks
 * "Deploy" in the page header.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button } from '../../../shared/ui/Button';
import { Card, CardBody, CardFooter, CardHeader } from '../../../shared/ui/Card';
import { FieldError } from '../../../shared/ui/FieldError';
import { Input } from '../../../shared/ui/Input';
import { Label } from '../../../shared/ui/Label';
import { Select } from '../../../shared/ui/Select';
import { useUpdateBusinessProfile } from '../../../features/business-profiles/useBusinessProfiles';
import {
  DeliveryModeEnum,
  EngineEnum,
  type BusinessProfileRecord,
} from '../../../features/business-profiles/types';

const overviewFormSchema = z.object({
  displayName: z.string().min(3).max(80),
  engine: EngineEnum,
  deliveryMode: DeliveryModeEnum,
  customerTerm: z.string().min(1).max(40),
  appointmentTerm: z.string().min(1).max(40),
  serviceTerm: z.string().min(1).max(40),
  staffTerm: z.string().min(1).max(40),
});
type OverviewFormValues = z.infer<typeof overviewFormSchema>;

export function OverviewTab({ record }: { record: BusinessProfileRecord }): JSX.Element {
  const { t } = useTranslation('adminProfiles');
  const updateMutation = useUpdateBusinessProfile(record.subVerticalKey);
  const [justSaved, setJustSaved] = useState(false);

  const defaults: OverviewFormValues = {
    displayName: record.profile.displayName,
    engine: record.profile.engine,
    deliveryMode: record.profile.deliveryMode,
    customerTerm: record.profile.terminology.customer ?? '',
    appointmentTerm: record.profile.terminology.appointment ?? '',
    serviceTerm: record.profile.terminology.service ?? '',
    staffTerm: record.profile.terminology.staff ?? '',
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
  } = useForm<OverviewFormValues>({
    resolver: zodResolver(overviewFormSchema),
    defaultValues: defaults,
  });

  useEffect(() => {
    reset(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id]);

  const onSubmit = async (values: OverviewFormValues) => {
    setJustSaved(false);
    await updateMutation.mutateAsync({
      displayName: values.displayName,
      engine: values.engine,
      deliveryMode: values.deliveryMode,
      terminology: {
        ...record.profile.terminology,
        customer: values.customerTerm,
        appointment: values.appointmentTerm,
        service: values.serviceTerm,
        staff: values.staffTerm,
      },
    });
    reset(values);
    setJustSaved(true);
    window.setTimeout(() => setJustSaved(false), 2500);
  };

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <CardHeader>
          <div>
            <h2 className="text-biz-lg biz-heading font-semibold">
              {t('overview.title')}
            </h2>
            <p className="text-biz-sm text-biz-muted-foreground mt-biz-content">
              {t('overview.hint')}
            </p>
          </div>
        </CardHeader>
        <CardBody className="space-y-biz-content-lg">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-biz-content-lg">
            <Field id="displayName" label={t('overview.displayNameLabel')}>
              <Input
                id="displayName"
                invalid={Boolean(errors.displayName)}
                {...register('displayName')}
              />
              <FieldError>{errors.displayName?.message}</FieldError>
            </Field>
            <div className="flex flex-col gap-biz-content">
              <Label>{t('overview.subVerticalKey')}</Label>
              <code className="block px-biz-content-lg py-biz-content text-biz-sm text-biz-muted-foreground bg-biz-muted rounded-biz-input min-h-biz-touch flex items-center font-mono">
                {record.subVerticalKey}
              </code>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-biz-content-lg">
            <Field id="engine" label={t('overview.engineLabel')}>
              <Select id="engine" {...register('engine')}>
                {EngineEnum.options.map((e) => (
                  <option key={e} value={e}>
                    {t(`engines.${e}` as const, { defaultValue: e })}
                  </option>
                ))}
              </Select>
            </Field>
            <Field id="deliveryMode" label={t('overview.deliveryModeLabel')}>
              <Select id="deliveryMode" {...register('deliveryMode')}>
                {DeliveryModeEnum.options.map((d) => (
                  <option key={d} value={d}>
                    {t(`deliveryModes.${d}` as const, { defaultValue: d })}
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          <SectionLabel>{t('overview.terminologyTitle')}</SectionLabel>
          <p className="text-biz-xs text-biz-muted-foreground">
            {t('overview.terminologyHint')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-biz-content-lg">
            <Field id="customerTerm" label={t('overview.customerTerm')}>
              <Input id="customerTerm" {...register('customerTerm')} />
              <FieldError>{errors.customerTerm?.message}</FieldError>
            </Field>
            <Field id="appointmentTerm" label={t('overview.appointmentTerm')}>
              <Input id="appointmentTerm" {...register('appointmentTerm')} />
              <FieldError>{errors.appointmentTerm?.message}</FieldError>
            </Field>
            <Field id="serviceTerm" label={t('overview.serviceTerm')}>
              <Input id="serviceTerm" {...register('serviceTerm')} />
              <FieldError>{errors.serviceTerm?.message}</FieldError>
            </Field>
            <Field id="staffTerm" label={t('overview.staffTerm')}>
              <Input id="staffTerm" {...register('staffTerm')} />
              <FieldError>{errors.staffTerm?.message}</FieldError>
            </Field>
          </div>
        </CardBody>
        <CardFooter>
          {justSaved ? (
            <span
              role="status"
              className="inline-flex items-center gap-1.5 text-biz-sm text-biz-success mr-auto"
            >
              <CheckCircle weight="regular" size={18} aria-hidden="true" />
              {t('common.saved')}
            </span>
          ) : null}
          <Button
            type="button"
            intent="secondary"
            onClick={() => reset(defaults)}
            disabled={!isDirty || isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          <Button type="submit" intent="primary" loading={isSubmitting} disabled={!isDirty}>
            {isSubmitting ? t('common.saving') : t('common.save')}
          </Button>
        </CardFooter>
      </form>
    </Card>
  );
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-biz-content">
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <h3 className="text-biz-xs font-semibold uppercase tracking-wider text-biz-muted-foreground pt-biz-content-lg">
      {children}
    </h3>
  );
}
